<template>
	<row-object-page :src-type="SrcType.PARTY" name="party" v-model="graph"
		 :build-endpoint="buildEndpoint"
		 :build-badge-url="buildBadgeEndpoint"
		 :extra-errors="extraErrors"
		 :force-info="true"
		 @edit="doEdit" @create="doCreate" @replace="doReplace"
		 ref="rowObject">
		<template #hero><duplicate-parties :data="graph" :disabled="false"></duplicate-parties></template>
	</row-object-page>
</template>

<script setup lang="ts">
import {ref, computed, reactive, onBeforeUnmount} from "vue";
import RowObjectPage from "@/pages/row-object/RowObjectPage.vue";
import {SourceType, SrcType} from "@t/meta/SourceType.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {SideMenu} from "@t/ui/Menu.ts";
import {injectEnv} from "@fd/Environment.ts";
import {PartyGraphWithMainTrinity} from "@t/customer/Customer.ts";
import {useRoute} from "vue-router";
import {param} from "@/router/Router.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import {PartyType} from "@t/customer/PartyType.ts";
import {oneOf} from "@t/fd/Lib.ts";
import {ErrorMap} from "@fd/PartInjectKeys.ts";
import DuplicateParties from "@fd/helpers/DuplicateParties.vue";
import {format, parseDate} from "@t/util/DateUtil.ts";

const ns = useNavigationStore();
const env = injectEnv();

const graph = ref<PartyGraphWithMainTrinity>(null);
const rowObject = ref<InstanceType<typeof RowObjectPage> | null>(null);
const id = computed<number>(() => graph.value?.party?.id ?? 0);
const route = useRoute();
const extraErrors = reactive<ErrorMap>([]);

function countMembersOfType(...types:PartyType[]):number {
	if (graph.value?.members?.length) {
		let count = 0;
		for (let item of graph.value.members) {
			if (oneOf(item.party.type, ...types)) count++;
		}
		return count;
	}
	return 0;
}

const peopleCount = computed<number>(() => {
	return countMembersOfType("INDIVIDUAL", "FREELANCER");
});

const branchCount = computed<number>(() => {
	return countMembersOfType("BRANCH");
});

function doReplace() {
	ns.replaceWith("/editParty.do", { id: graph.value.party.id });
}

function doEdit() {
	editInits();
	ns.chatContext = { url: "editParty.do?id="+graph.value.party.id, title: graph.value.party.fileAs, icon: graph.value.party.icon };
}

function doCreate() {
	ns.setEmptyMenu();
	ns.forward(env.msg('title.createCustomer'));
}

function editInits() {
	ns.setSideMenu(() => {
		const mb = ns.menuBuilder(rowObject.value.getContext());
		const menu:SideMenu = { groups: [] };
		menu.groups.push(mb.newRecordGroup(SrcType.PARTY, id.value, { mailMembers: true, facts: true, party: false }));

		// Can view sens
		if (hasAccess(graph.value.party, "sensitive")) {
			const views = mb.menuGroup("menu_views", "menu.title.views");
			if (Server.mod.email) views.items.push(mb.navItem("filterCustomerEmails", "filterCustomersObjects.do?obj=emails&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsEmails", "filter"));
			if (Server.mod.sms) views.items.push(mb.navItem("filterCustomerSMS", "filterCustomersObjects.do?obj=sms&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsSMS", null));
			if (Server.mod.opportunity) views.items.push(mb.navItem("filterCustomerOpportunities", "filterCustomersObjects.do?obj=opportunities&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsOpportunities", null));
			if (Server.mod.offer) views.items.push(mb.navItem("filterCustomerOffers", "filterCustomersObjects.do?obj=offers&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsOffers", null));
			if (Server.mod.communication) views.items.push(mb.navItem("filterCustomerContacts", "filterCustomersObjects.do?obj=contacts&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsContacts", null));
			views.items.push(mb.navItem("filterCustomerEvents", "filterCustomersObjects.do?obj=events&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsEvents", null));
			if (Server.mod.buscase) views.items.push(mb.navItem("filterCustomerBuscases", "filterCustomersObjects.do?obj=buscases&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsBusCases", null));
			if (Server.mod.product) views.items.push(mb.navItem("filterCustomerProducts", "filterCustomersObjects.do?obj=productcustomers&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsProducts", null));
			if (Server.mod.invoice) views.items.push(mb.navItem("filterCustomerInvoices", "filterCustomersObjects.do?obj=invoices&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsInvoices", null));
			if (Server.mod.buscase) views.items.push(mb.navItem("filterCustomerBuscaseItems", "filterBusCaseItems.do?_resetStack=no&id=" + id.value, "menu.filterBcItems", null));
			if (Server.mod.helpdesk) views.items.push(mb.navItem("filterCustomerProblems", "filterCustomersObjects.do?obj=problems&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsProblems", null));
			if (Server.mod.worksheet) views.items.push(mb.navItem("filterCustomerWorksheets", "filterCustomersObjects.do?obj=worksheets&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsWorksheets", null));
			if (peopleCount.value > 0) views.items.push(mb.navItem("filterCustomerPeople", "filterCustomersObjects.do?obj=people&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsPeople", null));
			if (branchCount.value > 0) views.items.push(mb.navItem("filterCustomerBranches", "filterCustomersObjects.do?obj=branches&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsBranches", null));
			menu.groups.push(views);
		}

		const tools = mb.menuGroup("menu_tools", "menu.title.tools");
		tools.items.push(mb.navItem("seeThis", "see.do?id="+id.value+"&type="+SrcType.PARTY, "menu.seeThis", "eye-eyebrow"));
		tools.items.push(mb.navItem("recordFiles", "showRecordFiles.do?srcId="+id.value+"&srcType="+SrcType.PARTY, "menu.recordFiles", "volume"));
		tools.items.push(mb.navItem("duplicateCustomer", "createParty.do?copy="+id.value, "menu.duplicateCustomer", "copy"));
		if (hasAccess(graph.value.party, "delete")) {
			tools.items.push(mb.confirmDelete(mb.navItem("deleteCustomer", "deleteCustomer.do?id="+id.value, "title.deleteCustomer", "trash"), "dial.deleteCustomer"));
		}
		menu.groups.push(tools);

		const prints = mb.menuGroup("menu_prints", "menu.title.prints");
		if (hasAccess(graph.value.party, "sensitive")) {
			prints.items.push(mb.navItem("printCustomer1", "outputParams.do?descriptor="+SrcType.PARTY+"&section="+ns.activeSection+"&pk="+id.value, "menu.printCustomer", "printer"));
			if (Server.mod.product) {
				let item = mb.navItem("printCustomerProducts", "printCustomerProducts.do?id="+id.value, "menu.printCustomerProducts", "printer");
				item.target = "_blank";
				prints.items.push(item);
			}
			prints.items.push(mb.navItem("profitMarginRatioSingle", "profitMarginRatioSingle.do?type="+SrcType.PARTY+"&id="+id.value, "menu.profitMarginRatio", "chart-pie"));
		}
		menu.groups.push(prints);
		return menu;
	}, null, true, "customer_edit", rowObject.value.getContext());
	ns.forward(graph.value.party.fileAs);
	delayedTimeout = setTimeout(doDelayedActions, 200);
}

function buildEndpoint(endpoint:string, edit:boolean, type:SourceType):string {
	if (!edit) {
		const type = param(route.query.t);
		if (type) endpoint = env.net.urlParam(endpoint, "type", type);
	}
	return endpoint;
}

function buildBadgeEndpoint(endpoint:string):string {
	const t = graph.value.trinity;
	if (t) {
		if (t.customer) endpoint = env.net.urlParam(endpoint, "subCustomer", t.customer);
		if (t.branch) endpoint = env.net.urlParam(endpoint, "subBranch", t.branch);
		if (t.individual) endpoint = env.net.urlParam(endpoint, "subPerson", t.individual);
	}
	return endpoint;
}

function insertExtraErrors(errs: ErrorMap) {
	extraErrors.length = 0;
	extraErrors.push(...errs);
	if (graph.value?.party?.isirStatus) {
		let isirDate = "-";
		if (graph.value?.party?.isirLastCheck) {
			const pd = parseDate(graph.value.party.isirLastCheck);
			if (pd.date) {
				isirDate = format(pd, env);
			}
		}
		extraErrors.push({
			level: "WARNING",
			message: env.msg('isir.warning', graph.value?.party?.isirUrl ?? "", graph.value.party.isirStatus, isirDate),
			icon: "binoculars",
			html:true
		});
	}
}

let delayedTimeout = 0;

function doDelayedActions() {
	delayedTimeout = 0;
	env.net.fetch("api/parties/"+id.value+"/warnings")
		.then((resp) => {
			if (resp.ok) {
				resp.json().then((e:ErrorMap) => {
					insertExtraErrors(e);
				})
			}
		});
}

onBeforeUnmount(() => {
	if (delayedTimeout) {
		clearTimeout(delayedTimeout);
		delayedTimeout = 0;
	}
});

</script>

<style>

</style>